import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import uploadResume from "../utils/uploadResume";
import emailjs from "@emailjs/browser";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { CheckCircle } from "lucide-react";

const JoinUs = () => {
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState(null);
  const [success, setSuccess] = useState(false);
  const [role, setRole] = useState("");
  const [roleLocked, setRoleLocked] = useState(false);

  const [searchParams] = useSearchParams();

  // 🔹 Auto-fill role from URL
  useEffect(() => {
    const roleParam = searchParams.get("role");
    if (roleParam) {
      setRole(roleParam);
      setRoleLocked(true);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resume || !role) return;

    setLoading(true);
    const form = e.target;

    try {
      // 1️⃣ Upload resume
      const resumeUrl = await uploadResume(resume);

      // 2️⃣ Save to Firestore
      await addDoc(collection(db, "applications"), {
        name: form.name.value,
        email: form.email.value,
        role,
        message: form.message.value,
        resumeUrl,
        createdAt: serverTimestamp(),
      });

      // 3️⃣ Email confirmation
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: form.name.value,
          email: form.email.value,
          role,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setSuccess(true);
    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please try again.");
    }

    setLoading(false);
  };

  // 🎉 Success Screen
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
        <div className="bg-white p-10 rounded-3xl shadow-xl text-center animate-fade-in">
          <CheckCircle className="mx-auto text-green-600 mb-4" size={64} />
          <h1 className="text-4xl font-bold mb-3">Application Submitted</h1>
          <p className="text-gray-600 max-w-md mx-auto">
            Thank you for applying to <strong>Skyreti EduCore</strong>.
            We’ll review your application and reach out via email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Join Our Team | Skyreti EduCore</title>
      </Helmet>

      <section className="pt-28 pb-24 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-xl mx-auto bg-white p-8 rounded-3xl shadow-xl">
          <h1 className="text-3xl font-bold text-center mb-2">
            Join Skyreti EduCore
          </h1>
          <p className="text-center text-gray-500 mb-8">
            We’re excited to learn more about you 🚀
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Full Name
              </label>
              <input
                name="name"
                required
                placeholder="John Doe"
                className="w-full border px-4 py-3 rounded-xl focus:ring-2 focus:ring-black outline-none"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                required
                placeholder="john@email.com"
                className="w-full border px-4 py-3 rounded-xl focus:ring-2 focus:ring-black outline-none"
              />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Role
              </label>

              {roleLocked ? (
                <div className="px-4 py-3 rounded-xl bg-gray-100 font-medium text-gray-800">
                  {role}
                </div>
              ) : (
                <select
                  required
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full border px-4 py-3 rounded-xl focus:ring-2 focus:ring-black outline-none"
                >
                  <option value="">Select a role</option>
                  <option>Frontend Developer</option>
                  <option>Backend Developer</option>
                  <option>Full Stack Developer</option>
                  <option>UI/UX Designer</option>
                  <option>Intern</option>
                </select>
              )}
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                About You
              </label>
              <textarea
                name="message"
                rows="4"
                placeholder="Tell us about your experience, skills, and goals..."
                className="w-full border px-4 py-3 rounded-xl focus:ring-2 focus:ring-black outline-none"
              />
            </div>

            {/* Resume */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Upload Resume
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                required
                onChange={(e) => setResume(e.target.files[0])}
                className="w-full"
              />
            </div>

            {/* Submit */}
            <button
              disabled={loading || !resume}
              className={`w-full py-3 rounded-xl font-semibold text-white transition-all ${
                loading || !resume
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-black hover:bg-gray-800"
              }`}
            >
              {loading ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
};

export default JoinUs;
